import React, { useState, useEffect, useMemo } from 'react';
import {
  getCachedAnimales, getCachedPesajes, getCachedServicios,
  getCachedPariciones, getLastCacheTime, getCacheStats,
} from '../services/offlineDB';
import { useAuth } from '../context/AuthContext';

const DashboardOfflinePage = () => {
  const { user } = useAuth();
  const [animales, setAnimales] = useState([]);
  const [pesajes, setPesajes] = useState([]);
  const [servicios, setServicios] = useState([]);
  const [pariciones, setPariciones] = useState([]);
  const [cacheTime, setCacheTime] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('resumen');

  useEffect(() => { loadOffline(); }, []);

  const loadOffline = async () => {
    setLoading(true);
    try {
      const estId = user?.establecimiento_id;
      const [a, p, s, par, ct, st] = await Promise.all([
        getCachedAnimales(estId), getCachedPesajes(), getCachedServicios(),
        getCachedPariciones(), getLastCacheTime(), getCacheStats(),
      ]);
      setAnimales(a); setPesajes(p); setServicios(s); setPariciones(par);
      setCacheTime(ct); setStats(st);
    } catch {}
    setLoading(false);
  };

  // Compute metrics locally from cached data
  const metrics = useMemo(() => {
    if (!animales.length) return null;

    const machos = animales.filter(a => a.sexo === 'macho');
    const hembras = animales.filter(a => a.sexo === 'hembra');

    // Peso promedio por animal (usando ultimo pesaje)
    const pesoByAnimal = {};
    const gmdByAnimal = {};
    pesajes.sort((a, b) => (b.fecha || '').localeCompare(a.fecha || ''));
    for (const p of pesajes) {
      if (!pesoByAnimal[p.animal_id]) {
        pesoByAnimal[p.animal_id] = p.peso;
        if (p.gmd != null) gmdByAnimal[p.animal_id] = p.gmd;
      }
    }

    const pesos = Object.values(pesoByAnimal);
    const pesoPromedio = pesos.length > 0 ? Math.round(pesos.reduce((a, b) => a + b, 0) / pesos.length) : 0;
    const pesoMax = pesos.length > 0 ? Math.max(...pesos) : 0;
    const pesoMin = pesos.length > 0 ? Math.min(...pesos) : 0;

    const gmds = Object.values(gmdByAnimal);
    const gmdProm = gmds.length > 0 ? (gmds.reduce((a, b) => a + b, 0) / gmds.length).toFixed(2) : null;

    // Categorias
    const categorias = {};
    animales.forEach(a => {
      const cat = a.categoria || 'sin_categoria';
      categorias[cat] = (categorias[cat] || 0) + 1;
    });

    // Top 5 / Bottom 5
    const animalConPeso = animales.map(a => ({
      ...a, peso: pesoByAnimal[a.id], gmd: gmdByAnimal[a.id],
    })).filter(a => a.peso);
    animalConPeso.sort((a, b) => b.peso - a.peso);

    const top5 = animalConPeso.slice(0, 5);
    const bottom5 = animalConPeso.slice(-5).reverse();

    // Prenez
    const prenadas = servicios.filter(s => s.resultado === 'preñada').length;
    const vacias = servicios.filter(s => s.resultado === 'vacia').length;
    const tasaPrenez = (prenadas + vacias) > 0 ? Math.round(prenadas / (prenadas + vacias) * 100) : null;

    // Destetes
    const conDestete = pariciones.filter(p => p.peso_destete && p.peso_destete > 0);
    const pesoDesteteProm = conDestete.length > 0 ? Math.round(conDestete.reduce((a, b) => a + b.peso_destete, 0) / conDestete.length) : null;

    // GMD negativas (alerta)
    const gmdNeg = Object.entries(gmdByAnimal).filter(([, v]) => v < 0);
    const gmdNegAnimals = gmdNeg.map(([id, gmd]) => {
      const a = animales.find(x => x.id === id);
      return { VID: a?.VID, gmd, peso: pesoByAnimal[id] };
    });

    return {
      total: animales.length, machos: machos.length, hembras: hembras.length,
      pesoPromedio, pesoMax, pesoMin, gmdProm,
      categorias, top5, bottom5, prenadas, vacias, tasaPrenez,
      totalPesajes: pesajes.length, totalServicios: servicios.length,
      totalPariciones: pariciones.length, pesoDesteteProm,
      gmdNegAnimals, conDestete: conDestete.length,
    };
  }, [animales, pesajes, servicios, pariciones]);

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="inline-block w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mb-3" />
          <p className="text-gray-500">Cargando datos locales...</p>
        </div>
      </div>
    );
  }

  if (!metrics || metrics.total === 0) {
    return (
      <div className="p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Dashboard Offline</h2>
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 text-center">
          <p className="text-amber-800 font-semibold mb-2">Sin datos locales</p>
          <p className="text-sm text-amber-600">Usa "Preparar Modo Campo" en el Dashboard principal para descargar los datos de tu rodeo.</p>
          <a href="/dashboard" className="mt-3 inline-block px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-semibold hover:bg-green-700 transition">Ir al Dashboard</a>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'resumen', label: 'Resumen' },
    { id: 'animales', label: `Animales (${metrics.total})` },
    { id: 'pesajes', label: `Pesajes (${metrics.totalPesajes})` },
  ];

  return (
    <div className="p-4 sm:p-6 space-y-5" data-testid="offline-dashboard">
      {/* Offline header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Dashboard Offline</h2>
          <p className="text-sm text-gray-500">Datos locales — sin conexion a internet</p>
        </div>
        <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-700">
          <span className="w-2 h-2 rounded-full bg-blue-500" />
          Cache: {cacheTime ? new Date(cacheTime).toLocaleString('es-AR') : 'Sin fecha'}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex bg-gray-100 rounded-lg p-1 overflow-x-auto">
        {tabs.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`px-3 sm:px-4 py-2 text-xs sm:text-sm rounded-md transition whitespace-nowrap ${tab === t.id ? 'bg-white text-blue-700 font-semibold shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
            data-testid={`offline-tab-${t.id}`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* TAB RESUMEN */}
      {tab === 'resumen' && (
        <div className="space-y-5" data-testid="offline-resumen">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <OCard label="Animales" value={metrics.total} sub={`${metrics.machos}M / ${metrics.hembras}H`} />
            <OCard label="Peso Promedio" value={`${metrics.pesoPromedio} kg`} sub={`Min: ${metrics.pesoMin} / Max: ${metrics.pesoMax}`} />
            <OCard label="GMD Promedio" value={metrics.gmdProm ? `${metrics.gmdProm} kg/d` : '—'} />
            <OCard label="Tasa Prenez" value={metrics.tasaPrenez ? `${metrics.tasaPrenez}%` : '—'} sub={`${metrics.prenadas} prenadas`} />
          </div>

          {/* Categorias */}
          <div className="bg-white rounded-xl shadow-sm p-5">
            <h3 className="font-bold text-gray-900 mb-3">Categorias</h3>
            <div className="flex flex-wrap gap-2">
              {Object.entries(metrics.categorias).map(([cat, count]) => (
                <span key={cat} className="px-3 py-1.5 bg-gray-100 rounded-full text-sm capitalize">{cat}: <strong>{count}</strong></span>
              ))}
            </div>
          </div>

          {/* Top/Bottom */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <div className="bg-white rounded-xl shadow-sm p-5">
              <h3 className="font-bold text-green-800 mb-3">Top 5 Peso</h3>
              {metrics.top5.map((a, i) => (
                <div key={a.id} className="flex justify-between p-2 border-b border-gray-100 last:border-0 text-sm">
                  <span><strong>{i+1}.</strong> VID {a.VID} ({a.sexo === 'macho' ? 'M' : 'H'} - {a.raza || '?'})</span>
                  <span className="font-bold text-green-700">{a.peso?.toFixed(0)} kg</span>
                </div>
              ))}
            </div>
            <div className="bg-white rounded-xl shadow-sm p-5">
              <h3 className="font-bold text-red-800 mb-3">Bottom 5 Peso</h3>
              {metrics.bottom5.map((a, i) => (
                <div key={a.id} className="flex justify-between p-2 border-b border-gray-100 last:border-0 text-sm">
                  <span>VID {a.VID} ({a.sexo === 'macho' ? 'M' : 'H'} - {a.raza || '?'})</span>
                  <span className="font-bold text-red-700">{a.peso?.toFixed(0)} kg</span>
                </div>
              ))}
            </div>
          </div>

          {/* GMD Negativas Alert */}
          {metrics.gmdNegAnimals.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4">
              <h3 className="font-bold text-red-800 mb-2">Animales perdiendo peso (GMD negativa)</h3>
              {metrics.gmdNegAnimals.map((a, i) => (
                <p key={i} className="text-sm text-red-700">- VID {a.VID}: GMD {a.gmd?.toFixed(2)} kg/dia ({a.peso?.toFixed(0)} kg)</p>
              ))}
            </div>
          )}

          <div className="grid grid-cols-3 gap-4">
            <OCard label="Pesajes" value={metrics.totalPesajes} />
            <OCard label="Servicios" value={metrics.totalServicios} />
            <OCard label="Pariciones" value={metrics.totalPariciones} />
          </div>
        </div>
      )}

      {/* TAB ANIMALES */}
      {tab === 'animales' && (
        <div className="bg-white rounded-xl shadow-sm overflow-hidden" data-testid="offline-animales-table">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  {['VID', 'IDE', 'Sexo', 'Raza', 'Categoria', 'Estado'].map(h => (
                    <th key={h} className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {animales.slice(0, 200).map((a, i) => (
                  <tr key={a.id} className={`border-b border-gray-100 ${i % 2 !== 0 ? 'bg-gray-50/50' : ''}`}>
                    <td className="py-2 px-4 font-semibold">{a.VID}</td>
                    <td className="py-2 px-4 text-gray-600">{a.IDE || '—'}</td>
                    <td className="py-2 px-4 capitalize">{a.sexo === 'macho' ? 'M' : 'H'}</td>
                    <td className="py-2 px-4">{a.raza || '—'}</td>
                    <td className="py-2 px-4 capitalize">{a.categoria || '—'}</td>
                    <td className="py-2 px-4">{a.estado}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* TAB PESAJES */}
      {tab === 'pesajes' && (
        <div className="bg-white rounded-xl shadow-sm overflow-hidden" data-testid="offline-pesajes-table">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  {['VID', 'Fecha', 'Peso (kg)', 'GMD', 'Altura', 'Frame'].map(h => (
                    <th key={h} className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {pesajes.sort((a, b) => (b.fecha || '').localeCompare(a.fecha || '')).slice(0, 200).map((p, i) => (
                  <tr key={p.id || i} className={`border-b border-gray-100 ${i % 2 !== 0 ? 'bg-gray-50/50' : ''}`}>
                    <td className="py-2 px-4 font-semibold">{p.VID || '—'}</td>
                    <td className="py-2 px-4">{p.fecha ? new Date(p.fecha).toLocaleDateString('es-AR') : '—'}</td>
                    <td className="py-2 px-4">{p.peso?.toFixed(1)}</td>
                    <td className="py-2 px-4">{p.gmd != null ? p.gmd.toFixed(2) : '—'}</td>
                    <td className="py-2 px-4">{p.altura_cadera || '—'}</td>
                    <td className="py-2 px-4">{p.frame || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

const OCard = ({ label, value, sub }) => (
  <div className="bg-white rounded-xl shadow-sm p-4 border-l-4 border-blue-400">
    <p className="text-xs font-medium text-gray-500 uppercase">{label}</p>
    <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
    {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
  </div>
);

export default DashboardOfflinePage;
